import { useMedplum } from '@medplum/react-hooks';
import type { Appointment } from '@medplum/fhirtypes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getZoomMeetingFromAppointment } from '@/lib/zoom/appointmentZoomMeeting';
import {
  clearIprHandoff,
  closeInPersonRecordingHostTab,
  createInPersonRecordingSessionId,
  navigateInPersonRecordingHostTab,
  openBlankInPersonRecordingHostTab,
  openInPersonRecordingChannel,
  openInPersonRecordingHostTab,
  readIprHandoff,
  refocusOpenerWindow,
  writeIprHandoff,
  type InPersonRecordingAudioDevice,
  type InPersonRecordingBridgeMessage,
  type InPersonRecordingBridgeMessageBody,
} from '@/lib/zoom/inPersonRecordingBridge';
import { formatUnknownError } from '@/lib/zoom/formatUnknownError';
import {
  endInPersonRecording,
  inPersonRecordingPath,
  isInPersonRecordingSessionActive,
  markInPersonRecordingJoined,
  startInPersonRecording,
} from '@/lib/zoom/inPersonRecordingService';

export type InPersonHostStatus =
  | 'idle'
  | 'starting'
  | 'connecting'
  | 'joined'
  | 'error'
  | 'tab-closed';

/** Setup wizard phase shown in the consent modal after confirm. */
export type InPersonRecordingSetupPhase =
  | 'consent'
  | 'setting-up'
  | 'connecting-host'
  | 'joining'
  | 'failed';

export interface UseInPersonRecordingSessionResult {
  hostStatus: InPersonHostStatus;
  setupPhase: InPersonRecordingSetupPhase;
  joinError: string | null;
  elapsedSeconds: number;
  isMuted: boolean;
  ending: boolean;
  starting: boolean;
  showRecordingControls: boolean;
  audioDevices: InPersonRecordingAudioDevice[];
  selectedAudioDeviceId: string | null;
  beginRecording: () => Promise<void>;
  openHostTabForAppointment: (appointment: Appointment) => void;
  toggleMute: () => void;
  setAudioInputDevice: (deviceId: string) => void;
  endSession: () => Promise<void>;
  clearSetupError: () => void;
}

function setupPhaseFromStatus(status: InPersonHostStatus): InPersonRecordingSetupPhase {
  switch (status) {
    case 'starting':
      return 'setting-up';
    case 'connecting':
      return 'connecting-host';
    case 'error':
      return 'failed';
    default:
      return 'consent';
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function closeHostWindow(appointmentId: string, hostTab: Window | null): void {
  try {
    if (hostTab) {
      if (!hostTab.closed) {
        hostTab.close();
      }
      return;
    }
  } catch {
    // fall through
  }
  closeInPersonRecordingHostTab(appointmentId);
}

/**
 * Appointment-page controller for in-person recording.
 * Stays on this tab; opens a script-opened COI host popup for Zoom.
 */
export function useInPersonRecordingSession(
  appointmentId: string | undefined,
  appointment: Appointment | null | undefined,
  onAppointmentUpdated?: (appointment: Appointment) => void,
): UseInPersonRecordingSessionResult {
  const medplum = useMedplum();
  const hostTabRef = useRef<Window | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const joinedAtRef = useRef<number | null>(null);
  const sessionIdRef = useRef<string>('');
  const meetingIdRef = useRef<string | null>(null);
  const abortPromiseRef = useRef<Promise<void> | null>(null);
  const resumedHandoffRef = useRef(false);
  const onUpdatedRef = useRef(onAppointmentUpdated);
  onUpdatedRef.current = onAppointmentUpdated;
  const medplumRef = useRef(medplum);
  medplumRef.current = medplum;

  const [hostStatus, setHostStatus] = useState<InPersonHostStatus>('idle');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [ending, setEnding] = useState(false);
  const [hostJoining, setHostJoining] = useState(false);
  const [audioDevices, setAudioDevices] = useState<InPersonRecordingAudioDevice[]>([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string | null>(null);

  const fhirActive = isInPersonRecordingSessionActive(appointment ?? undefined);
  const starting =
    hostStatus === 'starting' || hostStatus === 'connecting' || hostStatus === 'error';
  const showRecordingControls =
    fhirActive ||
    hostStatus === 'starting' ||
    hostStatus === 'connecting' ||
    hostStatus === 'joined' ||
    hostStatus === 'tab-closed' ||
    hostStatus === 'error';

  const setupPhase: InPersonRecordingSetupPhase =
    hostStatus === 'connecting' && hostJoining
      ? 'joining'
      : setupPhaseFromStatus(hostStatus);

  const postToHost = useCallback((
    message: InPersonRecordingBridgeMessageBody & { sessionId?: string },
  ) => {
    const sessionId = message.sessionId ?? sessionIdRef.current;
    if (!sessionId) return;
    channelRef.current?.postMessage({ ...message, sessionId });
  }, []);

  const refreshAppointment = useCallback(async (id: string) => {
    medplumRef.current.invalidateSearches('Appointment');
    const fresh = await medplumRef.current.readResource('Appointment', id, {
      cache: 'no-cache',
    });
    onUpdatedRef.current?.(fresh);
    return fresh;
  }, []);

  const resetLocalSession = useCallback(() => {
    meetingIdRef.current = null;
    joinedAtRef.current = null;
    setElapsedSeconds(0);
    setIsMuted(false);
    setAudioDevices([]);
    setSelectedAudioDeviceId(null);
    setJoinError(null);
    setHostJoining(false);
    setHostStatus('idle');
    hostTabRef.current = null;
  }, []);

  const abortFailedSetup = useCallback(
    async (message: string, sessionId: string) => {
      const id = appointmentId;
      if (!id) return;
      if (sessionId !== sessionIdRef.current) return;

      const run = (async () => {
        setJoinError(message);
        setHostStatus('error');
        setHostJoining(false);
        try {
          postToHost({ type: 'end', sessionId });
          closeHostWindow(id, hostTabRef.current);
          hostTabRef.current = null;
        } catch (e) {
          console.error('[InPersonRecording] abort failed setup:', e);
        }
      })();
      abortPromiseRef.current = run;
      try {
        await run;
      } finally {
        if (abortPromiseRef.current === run) {
          abortPromiseRef.current = null;
        }
      }
    },
    [appointmentId, postToHost],
  );

  const ensureChannel = useCallback(
    (id: string) => {
      if (channelRef.current) return channelRef.current;
      const channel = openInPersonRecordingChannel(id);
      channelRef.current = channel;
      if (!channel) return null;
      channel.onmessage = (event: MessageEvent<InPersonRecordingBridgeMessage>) => {
        const msg = event.data;
        if (!msg || typeof msg !== 'object' || !('type' in msg)) return;
        if (!('sessionId' in msg) || msg.sessionId !== sessionIdRef.current) return;
        switch (msg.type) {
          case 'host-hello':
            setHostJoining(true);
            setHostStatus((prev) => (prev === 'joined' ? prev : 'connecting'));
            break;
          case 'joined':
            joinedAtRef.current = msg.joinedAt;
            setHostJoining(false);
            setHostStatus('joined');
            setJoinError(null);
            refocusOpenerWindow();
            postToHost({ type: 'request-devices', sessionId: msg.sessionId });
            void (async () => {
              try {
                await markInPersonRecordingJoined(medplumRef.current, id);
                await refreshAppointment(id);
              } catch (e) {
                console.error('[InPersonRecording] mark joined failed:', e);
              }
            })();
            break;
          case 'join-error':
            void abortFailedSetup(msg.message, msg.sessionId);
            break;
          case 'mute-state':
            setIsMuted(msg.muted);
            break;
          case 'audio-devices':
            setAudioDevices(msg.devices);
            setSelectedAudioDeviceId(msg.selectedDeviceId);
            break;
          case 'host-closing':
            setHostStatus((prev) => (prev === 'idle' || prev === 'error' ? prev : 'tab-closed'));
            hostTabRef.current = null;
            break;
          case 'ended':
            resetLocalSession();
            if (appointmentId) clearIprHandoff(appointmentId);
            break;
          default:
            break;
        }
      };
      return channel;
    },
    [abortFailedSetup, appointmentId, postToHost, refreshAppointment, resetLocalSession],
  );

  useEffect(() => {
    if (!appointmentId) return undefined;
    ensureChannel(appointmentId);
    return () => {
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [appointmentId, ensureChannel]);

  // Resume session id after refresh while FHIR still shows an active recording.
  useEffect(() => {
    if (!appointmentId || resumedHandoffRef.current) return;
    const handoff = readIprHandoff(appointmentId);
    if (!handoff?.sessionId) return;

    resumedHandoffRef.current = true;
    sessionIdRef.current = handoff.sessionId;
    meetingIdRef.current = handoff.meetingId || meetingIdRef.current;
    ensureChannel(appointmentId);
  }, [appointmentId, ensureChannel]);

  useEffect(() => {
    if (hostStatus !== 'joined' || !joinedAtRef.current) return undefined;
    const tick = () => {
      if (joinedAtRef.current) {
        setElapsedSeconds(Math.floor((Date.now() - joinedAtRef.current) / 1000));
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [hostStatus]);

  useEffect(() => {
    if (fhirActive && hostStatus === 'idle') {
      setHostStatus('tab-closed');
    }
    if (!fhirActive && hostStatus === 'tab-closed') {
      setHostStatus('idle');
      setJoinError(null);
    }
  }, [fhirActive, hostStatus]);

  /**
   * Start: open blank host popup on the user gesture, then navigate it after start.
   * Stay on this appointment tab.
   */
  const beginRecording = useCallback(async () => {
    if (!appointmentId) return;
    if (abortPromiseRef.current) {
      await abortPromiseRef.current.catch(() => undefined);
    }
    const sessionId = createInPersonRecordingSessionId();
    sessionIdRef.current = sessionId;
    meetingIdRef.current = null;
    setHostStatus('starting');
    setHostJoining(false);
    setJoinError(null);

    ensureChannel(appointmentId);
    const hostTab = openBlankInPersonRecordingHostTab(appointmentId);
    hostTabRef.current = hostTab;
    refocusOpenerWindow();

    try {
      const started = await startInPersonRecording(medplum, appointmentId);
      if (sessionId !== sessionIdRef.current) return;
      meetingIdRef.current = started.meetingId;
      writeIprHandoff(appointmentId, {
        sessionId,
        meetingId: started.meetingId,
      });
      const hostUrl = inPersonRecordingPath(appointmentId, {
        ...started,
        sessionId,
      });
      if (hostTab && !hostTab.closed) {
        navigateInPersonRecordingHostTab(hostTab, hostUrl);
      } else {
        hostTabRef.current = openInPersonRecordingHostTab(hostUrl, appointmentId);
      }
      setHostStatus('connecting');
      refocusOpenerWindow();
    } catch (e) {
      if (sessionId !== sessionIdRef.current) return;
      closeHostWindow(appointmentId, hostTab);
      hostTabRef.current = null;
      setHostJoining(false);
      setJoinError(formatUnknownError(e));
      setHostStatus('error');
    }
  }, [appointmentId, ensureChannel, medplum]);

  const openHostTabForAppointment = useCallback(
    (appt: Appointment) => {
      if (!appointmentId) return;
      ensureChannel(appointmentId);
      const handoff = readIprHandoff(appointmentId);
      const sessionId =
        sessionIdRef.current || handoff?.sessionId || createInPersonRecordingSessionId();
      sessionIdRef.current = sessionId;
      const meeting = getZoomMeetingFromAppointment(appt);
      meetingIdRef.current = meeting.meetingNumber || handoff?.meetingId || null;
      if (meetingIdRef.current) {
        writeIprHandoff(appointmentId, {
          sessionId,
          meetingId: meetingIdRef.current,
        });
      }
      const url = inPersonRecordingPath(
        appointmentId,
        meeting.meetingNumber
          ? {
              meetingNumber: meeting.meetingNumber,
              password: meeting.password,
              sessionId,
            }
          : { meetingNumber: meetingIdRef.current ?? '', password: '', sessionId },
      );
      hostTabRef.current = openInPersonRecordingHostTab(url, appointmentId);
      setHostStatus('connecting');
      setHostJoining(false);
      refocusOpenerWindow();
    },
    [appointmentId, ensureChannel],
  );

  const toggleMute = useCallback(() => {
    postToHost({ type: 'set-mute', muted: !isMuted });
  }, [isMuted, postToHost]);

  const setAudioInputDevice = useCallback(
    (deviceId: string) => {
      const id = deviceId.trim();
      if (!id) return;
      setSelectedAudioDeviceId(id);
      postToHost({ type: 'set-audio-input', deviceId: id });
    },
    [postToHost],
  );

  const endSession = useCallback(async () => {
    if (!appointmentId || ending) return;
    setEnding(true);
    try {
      const expectedMeetingId = meetingIdRef.current ?? undefined;
      postToHost({ type: 'end' });
      await endInPersonRecording(medplum, appointmentId, { expectedMeetingId });
      await sleep(400);
      closeHostWindow(appointmentId, hostTabRef.current);
      hostTabRef.current = null;
      clearIprHandoff(appointmentId);
      resetLocalSession();
      await refreshAppointment(appointmentId);
    } finally {
      setEnding(false);
    }
  }, [appointmentId, ending, medplum, postToHost, refreshAppointment, resetLocalSession]);

  const clearSetupError = useCallback(() => {
    setJoinError(null);
    setHostJoining(false);
    setHostStatus('idle');
  }, []);

  return {
    hostStatus,
    setupPhase,
    joinError,
    elapsedSeconds,
    isMuted,
    ending,
    starting,
    showRecordingControls,
    audioDevices,
    selectedAudioDeviceId,
    beginRecording,
    openHostTabForAppointment,
    toggleMute,
    setAudioInputDevice,
    endSession,
    clearSetupError,
  };
}
